import { MarketData } from '@/hooks/query';
import { useEffect, useState, Fragment } from 'react';
import { ProvidedMarket } from '@/types/menu-setting';
import { useTranslations } from 'next-intl';
import { useRouter } from '@/navigation';
import { useLocale } from 'next-intl';
import { useAppearanceStore } from '@/store/appearance';
import { useAnalyseAttribute } from '@/features/next-analyse';
import clsx from 'clsx';
import { homeStyle } from './home-page.style';
import { Icon } from '@/components/icons/svg-icons';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { KafkaNote } from '@nf/utils-common/constants';
import { ViewEnum } from '@/types';
import { useAccountInfo } from '@/hooks/query/account';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { switchViewService } from '@/services/swtich-view.service';
import { SiteConfigsSettingsData } from '@nf/types/site';
import { siteService } from '@/services/site.service';
import { sportService } from '@/services/sport.service';
import { useAuthToken } from '@/store/auth';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { Breakpoint } from '@/features/breakpoint';
import { TemplateResource } from '@/types/template-resource';
import { modalService } from '@/services/modal.service';
import { useCasinoRoyale } from '@/hooks/query/use-casino-royale';
import { useSiteStore } from '@/store/site';
import { NonSportProductProps } from '../products-wide';
import { defaultImageService } from '@/services/jupiter/default-image-service';
import { BetBuilderPlusIcon } from '@/components/icons/bet-builder-plus-icon';
import { useOddsPageSwitcher } from '@/hooks/use-odds-page-switcher';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { NewFeatureBadge } from '@/components/new-tag/new-feature-badge';
import { ExclusiveActivityBanner } from '@/features/home-page/exclusive-activity-banner';
import { WcProductTag } from '@/features/wc-promotion-2026/components/wc-tag/wc-tag';
import { useWcPromotionVisible } from '@/domains/wc-promotion/hooks/use-wc-promotion-visible';
import { TaskTypeEnum } from '@/domains/wc-promotion/types';
import { wcPromotionMissionService } from '@/domains/wc-promotion/services/wc-promotion-mission.service';

const marketDefinition: { [key: string]: { label: string, index: number, isBetBuilder?: boolean } } = {
	L: {
		label: 'lbl_live',
		index: 0
	},
	T: {
		label: 'lbl_today',
		index: 1
	},
	E: {
		label: 'lbl_early',
		index: 2
	},
	P: {
		label: 'lbl_mixparlay',
		index: 3
	},
	BB: {
		label: 'lbl_betbuilderplus',
		index: 4,
		isBetBuilder: true
	}
};
const sportEnNameResourceKeys: { [key: string]: string } = {
	sport1: 'Soccer',
	sport997: 'Soccer',
	sport2: 'Basketball',
	sport993: 'Basketball',
	sport3: 'Football',
	sport4: 'Ice Hockey',
	sport5: 'Tennis',
	sport6: 'Volleyball',
	sport7: 'Snooker',
	sport8: 'Baseball',
	sport9: 'Badminton',
	sport10: 'Golf',
	sport11: 'Motorsports',
	sport12: 'Swimming',
	sport13: 'Politics',
	sport14: 'WaterPolo',
	sport15: 'Diving',
	sport16: 'Boxing',
	sport17: 'Archery',
	sport18: 'Table Tennis',
	sport19: 'Weightlifting',
	sport20: 'Canoeing',
	sport21: 'Gymnastics',
	sport22: 'Athletics',
	sport23: 'Equestrian',
	sport24: 'Handball',
	sport25: 'Darts',
	sport26: 'Rugby',
	sport27: 'Cricket',
	sport28: 'Field Hockey',
	sport29: 'Winter Sports',
	sport30: 'Squash',
	sport31: 'Entertainment',
	sport32: 'Netball',
	sport33: 'Cycling',
	sport34: 'Fencing',
	sport35: 'Judo',
	sport36: 'M. Pentathlon',
	sport37: 'Rowing',
	sport38: 'Sailing',
	sport39: 'Shooting',
	sport40: 'Taekwondo',
	sport41: 'Triathlon',
	sport42: 'Wrestling',
	sport43: 'E-Sports',
	sport44: 'Muay Thai',
	sport45: 'Beach Volleyball',
	sport47: 'Kabaddi',
	sport48: 'SepakTakraw',
	sport49: 'Futsal',
	sport50: 'Cricket',
	sport51: 'Beach Soccer',
	sport52: 'Poker',
	sport53: 'Chess',
	sport54: 'Olympics',
	sport55: 'Finance',
	sport99: 'Other Sports',
	sport161: 'Number Game',
	sport164: 'Happy 5',
	sport175: 'BitCoin',
};

const getProductGameImageId = (gameId: string | number, enableFilterSaba?: boolean): string => {
	const id = String(gameId);
	if (id === 'sport997' && enableFilterSaba) {
		return 'sport997-special';
	}
	return id;
};

const LiveIcon = () => (
	<div className="live-icon-animation">
		<Icon name="filter-live" />
	</div>
);
export type ProductProps = {
	eventMode?: string,
	GameId: number | string,
	GlobalResourceLabel: string,
	BackgroundName?: string,
	Count?: number,
	RouteTarget?: string,
	IsLive?: boolean,
	forAnalyse?: boolean,
	currentMarket?: string,
	isComingSoon?: boolean,
	isNew?: boolean,
};

const MobileProduct = ({ eventMode, IsLive, GameId, GlobalResourceLabel, BackgroundName, Count, RouteTarget, forAnalyse = false, currentMarket, isComingSoon = false, isNew = false }: ProductProps) => {
	const t = useTranslations('General');

	const view = useAppearanceStore(state => state.view);
	const router = useRouter();
	const locale = useLocale();
	const taggedElement = useAnalyseAttribute(forAnalyse ? 'switch-to-odds-page-via-lobby' : '');
	const { SiteConfigsData } = useGetSiteConfigs();
	const { switchToBetBuilderPlus, switchToParlay, switchToMatches } = useOddsPageSwitcher();
	const isWcPromotionVisible = useWcPromotionVisible();

	const clickProduct = () => {
		setKafka();
		const isBetBuilderPlusTab = currentMarket === 'BB';
		const isParlayTab = currentMarket === 'P';

		if (RouteTarget) {
			if (isBetBuilderPlusTab) {
				switchToBetBuilderPlus();
			} else if (isParlayTab) {
				switchToParlay();
			} else {
				switchToMatches();
			}
			router.push(RouteTarget, { locale })
		}
	}

	const setKafka = () => {

		let action;
		if (view == ViewEnum.mars && window.innerWidth < 750) {
			if (GameId === 'sport1') {
				action = 23; // sports > soccer
			}
			if (GameId === 'sport2') {
				action = 24; // sports > 
			}
			if (action) {
				const kafkaData = {
					note: KafkaNote.MarsV1,
					type: 1,
					action
				}
				setKafkaNormal(kafkaData);
			}
		}
		if (view === ViewEnum.galaxy && window.innerWidth < 750) {
			if (GameId === 'sport1') {
				setKafkaNormal({ note: KafkaNote.GalaxyV2, type: 16, action: 2 });
			}
		}
		if (view !== ViewEnum.mars || window.innerWidth < 750) return; // 只計 mars 且寬度 750 以上
		if (GameId === 'sport1') {
			switch (eventMode) {
				case 'M0':
					action = 21; // sports > soccer
					break;
				case 'M3':
					action = 6; // euro-cup > soccer
					break;
			}
		}
		if (action) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: 1,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	return (
		<div
			className={clsx('home-product__game', homeStyle.gameItem)}
			data-live={IsLive}
			data-game={GameId}
			onClick={clickProduct}
			ref={taggedElement}
			data-disabled={isComingSoon}
		>
			{isNew ? <NewFeatureBadge /> : null}
			{isComingSoon ? <div className={clsx('home-product__game-tag', homeStyle.gameTag)}>{t('lbl_ComingSoon')}</div> : null}
			<div className="home-product__game-shadow" />
			<div className={clsx('home-product__game-content', homeStyle.gameContent)}>
				{
					!IsLive ? null : <LiveIcon />
				}
				<div
					className="home-product__game-deco"
					style={{
						backgroundImage: `url(${siteService.combineImagePath('/images/home/product/bg_deco.png', true, SiteConfigsData)})`
					}}
				/>
				<div className={clsx('home-product__game-border', homeStyle.gameGradientBg)}>
					<div
						className="home-product__game-border-bg"
						style={{
							backgroundImage: `url(${siteService.combineImagePath('/images/home/product/bg_border.png', true, SiteConfigsData)})`
						}}
					/>
				</div>
				<div
					className="home-product__game-img"
					style={{
						backgroundImage: `url(${siteService.combineImagePath(`/images/home/product-game/${getProductGameImageId(GameId, SiteConfigsData?.EnableFilterSaba)}.webp`, true, SiteConfigsData)})`
					}}
				/>
				<div className="home-product__game-text">
					<div className="home-product__game-title">{GlobalResourceLabel}</div>
					{
						(!Count || isComingSoon) ? null : <div className="home-product__game-num">{Count}</div>
					}
				</div>
				{GameId === 'sport1' && isWcPromotionVisible ? <WcProductTag /> : null}
			</div>
		</div>
	);
}
const MobileNonSportProduct = ({ eventMode, IsLive, GameId, GlobalResourceLabel, BackgroundName, Count, RouteTarget, forAnalyse = false, Url, Name, Sort, isNew = false }: NonSportProductProps) => {
	const view = useAppearanceStore(state => state.view);
	const router = useRouter();
	const locale = useLocale();
	const taggedElement = useAnalyseAttribute(forAnalyse ? 'switch-to-odds-page-via-lobby' : '');
	const { SiteConfigsData } = useGetSiteConfigs();

	const clickProduct = () => {
		setKafka();
		if (RouteTarget) {
			router.push(RouteTarget, { locale })
		}
	}

	const setKafka = () => {
		let action;
		if (view == ViewEnum.mars && window.innerWidth < 750) {
			if (GameId === 'sport1') {
				action = 23; // sports > soccer
			}
			if (GameId === 'sport2') {
				action = 24; // sports > 
			}
			if (action) {
				const kafkaData = {
					note: KafkaNote.MarsV1,
					type: 1,
					action
				}
				setKafkaNormal(kafkaData);
			}
		}
		if (view !== ViewEnum.mars || window.innerWidth < 750) return; // 只計 mars 且寬度 750 以上
		if (GameId === 'sport1') {
			switch (eventMode) {
				case 'M0':
					action = 21; // sports > soccer
					break;
				case 'M3':
					action = 6; // euro-cup > soccer
					break;
			}
		}
		if (action) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: 1,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	return (
		!Url?
			<div
				className={clsx('home-product__game', homeStyle.gameItem)}
				data-live={IsLive}
				data-game={GameId}
				onClick={clickProduct}
				ref={taggedElement}
			>
				{isNew ? <NewFeatureBadge /> : null}
				<div className="home-product__game-shadow" />
				<div className={clsx('home-product__game-content', homeStyle.gameContent)}>
					{
						!IsLive ? null : <LiveIcon />
					}
					<div className="home-product__game-deco" />
					<div className={clsx('home-product__game-border', homeStyle.gameGradientBg)}>
						<div
							className="home-product__game-border-bg"
							style={{
								backgroundImage: `url(${siteService.combineImagePath('/images/home/product/bg_border.png', true, SiteConfigsData)})`
							}}
						/>
					</div>
					<div
						className="home-product__game-img"
						style={{
							backgroundImage: `url(${siteService.combineImagePath(`/images/home/product-game/${getProductGameImageId(GameId, SiteConfigsData?.EnableFilterSaba)}.webp`, true, SiteConfigsData)})`
						}}
					/>
					<div className="home-product__game-text">
						<div className="home-product__game-title">{GlobalResourceLabel}</div>
					</div>
				</div>
			</div> :
			<div
				className={clsx('home-product__game', homeStyle.gameItem)}
				data-live={false}
				onClick={clickProduct}
			>
				{isNew ? <NewFeatureBadge /> : null}
				<div className="home-product__game-shadow" />
				<div className={clsx('home-product__game-content', homeStyle.gameContent)}>
					{
						!IsLive ? null : <LiveIcon />
					}
					<div className="home-product__game-deco" />
					<div className={clsx('home-product__game-border', homeStyle.gameGradientBg)} />
					<div className="home-product__game-img" style={{ backgroundImage: `url(${SiteConfigsData?.ImageServerUrl}${Url})` }} />
					<div className="home-product__game-text">
						<div className="home-product__game-title">{Name}</div>
					</div>
				</div>
			</div>
	);
}
export const ThirdPartyProducts = ({ productProps }: { productProps: NonSportProductProps[] }) => {
	const { nonSport } = useLicSettings();
	const virtualSoccerList = nonSport?.virtualSoccerList ?? [];
	
	const routedProducts = productProps.map((productProp) => {
		if (productProp.GameId === 'virtual180' || productProp.GameId === 180) {
			return {
				...productProp,
				RouteTarget: sportService.resolveVirtual180RouteTarget(productProp.RouteTarget, virtualSoccerList),
			};
		}
		return productProp;
	});

	// 篩選 GameId 為 180 的產品：如果 virtualSoccerList 為空則隱藏，否則檢查是否包含 [108,109,180]
	const filteredProducts = routedProducts.filter((productProp) => {
		if (productProp.GameId === 'virtual180' || productProp.GameId === 180) {
			// 如果 virtualSoccerList 是空陣列，直接隱藏
			if (sportService.isVirtualSoccerListEmpty(virtualSoccerList)) {
				return false;
			}
		}
		return true;
	});
	
	return (
		<div className="home-product">
			<div className="home-product__list">
				{
					filteredProducts.map((productProp, productIndex) => (
						<MobileNonSportProduct
							key={`${productProp.GlobalResourceLabel}_${productIndex}`}
							{...productProp}
						/>
					))
				}
			</div>
		</div>
	);
};
export const TemplateProducts = ({ templateResource }: { templateResource: TemplateResource }) => {
	const subProductResources = templateResource?.SubProductResources? templateResource.SubProductResources: [];
	const isBefore = !useAuthToken();
	const { SiteConfigsData } = useGetSiteConfigs();
	const { openCasinoRoyale } = useCasinoRoyale();
	const deepLinkSiteInfo = useSiteStore(state => state.deepLinkSiteInfo);
	
	return (
		<div className="home-product">
			<div className="home-product__list">
				{
					subProductResources.map((subProductResource, productIndex) => {
						return (
							<div
								key={`${subProductResource.Name}_${productIndex}`}
								className={clsx('home-product__game', homeStyle.gameItem)}
								data-live={false}
								onClick={() => {
									if (isBefore) {
										modalService.openLoginModal();
										return;
									}
									if (subProductResource.TheURL) {
										openCasinoRoyale({
											category: subProductResource.TheURL,
											isApp: deepLinkSiteInfo.isApp
										});
									} else {
										const sportId = subProductResource.SportId ? subProductResource.SportId?.split('-')[0] : '';
										const betType = subProductResource.BetType ? String(subProductResource.BetType) : '0';
										openCasinoRoyale({
											sportTypeId: sportId,
											betTypeId: betType,
											category: subProductResource.TheURL || undefined,
											isApp: deepLinkSiteInfo.isApp
										});
									}
								}}
							>
								<div className="home-product__game-shadow" />
								<div className={clsx('home-product__game-content', homeStyle.gameContent)}>
									<div className="home-product__game-deco" />
									<div className={clsx('home-product__game-border', homeStyle.gameGradientBg)} />
									{
										<MobileProductImage 
											Url={subProductResource.ClassName}
											SiteConfigsData={SiteConfigsData}
										/>
									}
									<div className="home-product__game-text">
										<div className="home-product__game-title">{subProductResource.Name}</div>
									</div>
								</div>
							</div>
						)
						
					})
				}
			</div>
		</div>
	);
}
const getAvailableMarketsInPriority = (matchCountOfMarkets: Record<string, number>): string[] => {
	return Object.keys(matchCountOfMarkets)
		.filter(market => {
			return market in marketDefinition && (matchCountOfMarkets[market] ?? 0) > 0;
		})
		.sort((marketA, marketB) => marketDefinition[marketA].index - marketDefinition[marketB].index);
};
const STREAMER_ASSET_LOCALE_LIST = ['beng', 'ch', 'cs', 'zhcn', 'en', 'id', 'jp', 'ko', 'th', 'vn'];
type ProductsProps = {
	isLoading: boolean,
	currentEventMode: string | undefined,
	currentMarket: string | undefined,
	currentRoutePrefix: string | undefined,
	matchCountOfMarkets: Record<string, number>,
	setCurrentMarket: (market: string) => void,
	availableProducts: MarketData[],
	commingSoccerName: string
};
export const ProductsWithMarkets = ({
	isLoading,
	currentEventMode,
	currentMarket,
	currentRoutePrefix,
	matchCountOfMarkets,
	setCurrentMarket,
	availableProducts,
	commingSoccerName
}: ProductsProps) => {
	const t = useTranslations('General');
	const view = useAppearanceStore(state => state.view);
	const router = useRouter();
	const locale = useLocale();
	const { user } = useAccountInfo();
	const { SiteConfigsData } = useGetSiteConfigs();
	const shortCode = switchViewService.convertLanguageToShortCode(user?.Lang as string, SiteConfigsData as SiteConfigsSettingsData);
	const streamerAssetLocale = STREAMER_ASSET_LOCALE_LIST.includes(shortCode) ? (shortCode === 'zhcn' ? 'cs' : shortCode) : 'en';
	const { breakpoint } = useBreakpoint();
	const { enabledBetBuilderPlusSportIDs, enableStreamerBanner } = useLicSettings();

	// 活動更換主題圖
	const streamerAssetSrc = enableStreamerBanner
		? `/images/home/streamer/world-cup-2026/${streamerAssetLocale}-${(breakpoint <= Breakpoint.sm) ? 'mobile' : 'desktop'}.webp`
		: `/images/home/product/streamer/${streamerAssetLocale}/streamer-${(breakpoint <= Breakpoint.sm) ? 'mobile' : 'desktop'}.webp`;
	
	useEffect(() => {
		const availableMarketsInPriority = getAvailableMarketsInPriority(matchCountOfMarkets);
		if (availableMarketsInPriority.length > 0) {
			if (!currentMarket || availableMarketsInPriority.every(availableMarket => availableMarket !== currentMarket)) {
				setCurrentMarket(availableMarketsInPriority[0]);
			}
		}
	}, [matchCountOfMarkets]);

	const clickMarket = (market: string) => {
		setKafka(market);
		setCurrentMarket(market);
		if (market === 'BB') {
			wcPromotionMissionService.notifyMission({
				taskType: TaskTypeEnum.ClickSabaFeatures,
				productCode: 'bet-builder'
			});
		}
		if (market === 'P') {
			wcPromotionMissionService.notifyMission({
				taskType: TaskTypeEnum.ClickSabaFeatures,
				productCode: 'parlay'
			});
		}
	}

	const setKafka = (market: string) => {
		if (view !== ViewEnum.mars || window.innerWidth >= 750) return; // 只計 mars 且寬度小於 750

		let action;
		switch (market) {
			case 'L': // live
				action = 7;
				break;
			case 'T': // today
				action = 8;
				break;
			case 'E': // early
				action = 9;
				break;
			case 'P': // parlay
				action = 10;
				break;
		}
		if (action) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: 1,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const goToStreamer = (view: ViewEnum) => {
		const isMobile = breakpoint <= Breakpoint.sm;

		const getKafkaData = (view: ViewEnum) => {
			switch (view) {
				case ViewEnum.mars:
					return {
						note: KafkaNote.MarsV1,
						type: 5,
						action: isMobile ? 7 : 11
					};
				case ViewEnum.galaxy:
					return {
						note: KafkaNote.GalaxyV2,
						type: 13,
						action: isMobile ? 7 : 10
					};
				default:
					return null;
			}
		};
	
		const kafkaData = getKafkaData(view);
	
		if (kafkaData) {
			setKafkaNormal(kafkaData);
		}
		router.push('/streamer', { locale });
	}

	const EuroCupPromotionBanner = () => {
		return (
			<div className="home-activity-banner" onClick={() => router.push('/promotion')}>
				<img src={siteService.combineImagePath(`/images/banner/home/${shortCode}/pm_euro-cup.jpg`, true, SiteConfigsData)} />
			</div>
		)	
	}

	let sortedAvailableProducts = [...availableProducts];
	const sportOrderList = [1, 2, 6, 9, 12, 22, 16, 43, 21, 19, 17];
	if (currentEventMode == 'M14') {
		sortedAvailableProducts = sortedAvailableProducts.sort((a, b) => {
			const aIndex = sportOrderList.indexOf(a.GameId ?? 0);
			const bIndex = sportOrderList.indexOf(b.GameId ?? 0);
			if (aIndex === -1 && bIndex === -1) return 0;
			if (aIndex === -1) return 1;
			if (bIndex === -1) return -1;
			return aIndex - bIndex;
		});
	}

	return (
		<div className={clsx('home-product', homeStyle.product)}>
			<div className={clsx('home-product__nav', homeStyle.productNav)}>
				{
					Object.keys(matchCountOfMarkets)
						.filter(market => {
							return market in marketDefinition && (matchCountOfMarkets[market] ?? 0) > 0;
						})
						.sort((marketA, marketB) => marketDefinition[marketA].index - marketDefinition[marketB].index)
						.map(market => {
							const isLiveMarket = market === 'L';
							const isBetBuilder = marketDefinition[market].isBetBuilder ?? false;
							const isSelected = market === currentMarket;
							return (
								<div
									key={market}
									className={clsx('home-product__nav-btn', homeStyle.productNavItem, isBetBuilder ? 'betbuilderplus' : '')}
									data-active={isSelected}
									data-live={isLiveMarket}
									onClick={() => clickMarket(market)}
								>
									{
										!isLiveMarket ? null : <LiveIcon />
									}
									{isBetBuilder ? (
										<div className="home-product__nav-btn-icon">
											<BetBuilderPlusIcon variant="white" />
										</div>
									) : null}
									<div className="home-product__nav-title">{t(marketDefinition[market].label)}</div>
									<div className="home-product__nav-num">{matchCountOfMarkets[market]}</div>
									<div
										className="home-product__nav-btn-bg"
										style={{
											backgroundImage: `url(${siteService.combineImagePath('/images/home/product/bg_btn.png', true, SiteConfigsData)})`
										}}
									/>
								</div>
							);
						})
				}
				{SiteConfigsData?.EnableStreamerSchedule &&
					<div
						className={clsx('home-product__nav-btn streamer-schedule', homeStyle.productNavItem)}
						style={{ backgroundImage: `url(${siteService.combineImagePath(streamerAssetSrc, true, SiteConfigsData)})` }}
						onClick={() => goToStreamer(view)}
					>
					</div>
				}
			</div>
			<div className={clsx('home-product__list', homeStyle.gameList)}>
				<ExclusiveActivityBanner />
				{
					isLoading ? (
						<div
							className={clsx('home-product__game', homeStyle.gameItem)}
							data-loading={isLoading}
						>
							<div className="home-product__game-shadow" />
							<div className={clsx('home-product__game-content', homeStyle.gameContent)}>
								<div className="loading-circle">
									<Icon name="loading" />
								</div>
							</div>
						</div>
					) : sortedAvailableProducts
						.filter(marketData => sportEnNameResourceKeys[`sport${marketData.GameId}`] !== undefined)
						.filter((marketData, productIndex) => productIndex < 21)
						.filter(marketData => (currentMarket === 'BB' && marketData.GameId === 1 && !enabledBetBuilderPlusSportIDs?.includes(1)) ? false : true)
						.map(marketData => {
							const isCurrentlyShowingLive = currentMarket === 'L';
							const { GameId, Name } = marketData;
							const preferredMarket = (currentMarket !== 'P' && currentMarket !== 'BB') ? currentMarket : ['L', 'T', 'E'].find((filterableMarket: string): boolean => (marketData[filterableMarket as ProvidedMarket] ?? 0) > 0);
							const count = (currentMarket === 'P' || currentMarket === 'BB') ? ['E', 'L', 'T'].reduce((sum, market) => sum += marketData[market as ProvidedMarket], 0) : marketData[currentMarket as ProvidedMarket];
							return (
								<Fragment key={GameId}>
									<MobileProduct
										key={GameId}
										eventMode={currentEventMode}
										GameId={`sport${GameId}`}
										GlobalResourceLabel={Name}
										BackgroundName={sportEnNameResourceKeys[`sport${GameId}`]}
										IsLive={isCurrentlyShowingLive}
										RouteTarget={(!preferredMarket || !currentRoutePrefix) ? '' : (isCurrentlyShowingLive && currentRoutePrefix === 'sports') ? `/live/${GameId}` : `/${currentRoutePrefix}/${GameId}/${preferredMarket.toLowerCase()}`}
										Count={count}
										forAnalyse={true}
										currentMarket={currentMarket}
									/>
									{/* hardcode 寫死soccer coming soon，未來可能需要修改 */}
									{GameId === 2 && currentMarket === 'BB' && !enabledBetBuilderPlusSportIDs?.includes(1) ? 
										(
											<MobileProduct
												key={'coming-soon-soccer'}
												eventMode={'MO'}
												GameId={'sport1'}
												GlobalResourceLabel={commingSoccerName}
												currentMarket={'BB'}
												BackgroundName={sportEnNameResourceKeys['sport1']}
												IsLive={false}
												isComingSoon={true}
											/>
										) : null}
								</Fragment>
							);
						})
				}
			</div>
		</div>
	);
};

type MobileProductImageProp = {
	Url?: string,
	SiteConfigsData?: SiteConfigsSettingsData |null
}

const MobileProductImage = ({ Url, SiteConfigsData }: MobileProductImageProp) => {
	const [Image, setImage] = useState<string>('');
	useEffect(() => {
		const imageUrl =`${Url}`;
		defaultImageService.validateImageUrl(imageUrl, SiteConfigsData?.ImageServerUrl).then((url) => {
			setImage(url);
		})
	}, [Url, SiteConfigsData])
	return (
		<div
			className="home-product__game-img"
			style={{ backgroundImage: `url(${Image})` }}
		/>
	);
};
  